import { useCallback } from 'react';
import { terminalCommands } from '../data/mockData';
import { trackVisitor } from '../utils/analytics';
import { storeLanguage } from '../utils/locale';
import { web3Vulnerabilities, hackingQuotes } from '../data/terminalData';

/**
 * Custom hook for processing terminal commands.
 * Admin mode has been fully removed from v3.1.1.
 */
export const useCommandProcessor = ({
  currentData,
  language,
  setLanguage,
  output,
  setOutput,
  setInput,
  addToHistory,
  setActiveSection,
  setIsMatrixMode,
  setShowCTFPanel,
  t
}) => {
  // Track first visit (only once per session)
  trackVisitor();

  const processCommand = useCallback(async (cmd) => {
    const trimmedCmd = cmd.trim().toLowerCase();
    const baseCommand = trimmedCmd.split(' ')[0];

    if (!trimmedCmd) return;

    addToHistory(cmd);

    // Special: clear preserves the welcome hero
    if (baseCommand === 'clear') {
      const welcomeMessages = [
        {
          type: 'hero',
          logoSrc: currentData.logo,
          tagline: currentData.title
        },
        { type: 'text', content: `${t.welcome} ${currentData.name}` },
        { type: 'text', content: `${currentData.title}` },
        { type: 'text', content: '' },
        { type: 'text', content: t.helpText },
        { type: 'text', content: t.navText },
        { type: 'text', content: '' },
        { type: 'text', content: t.tipText },
        { type: 'text', content: '' }
      ];
      setOutput(welcomeMessages);
      setInput('');
      return;
    }

    const newOutput = [...output, { type: 'command', content: cmd }];

    switch (baseCommand) {
      case 'help':
        newOutput.push({
          type: 'text',
          content: `${t.commandsAvailable}\n` +
            Object.entries(t.commands).map(([c, desc]) => `  ${c.padEnd(15)} - ${desc}`).join('\n')
        });
        break;

      case 'whoami':
        newOutput.push({ type: 'whoami-banner' });
        break;

      case 'about':
        setActiveSection('about');
        newOutput.push({ type: 'success', content: language === 'es' ? '→ Cargando sección "about"...' : '→ Loading "about" section...' });
        break;

      case 'experience':
        setActiveSection('experience');
        newOutput.push({ type: 'success', content: language === 'es' ? '→ Cargando experiencia...' : '→ Loading experience...' });
        break;

      case 'education':
        setActiveSection('education');
        newOutput.push({ type: 'success', content: language === 'es' ? '→ Cargando educación...' : '→ Loading education...' });
        break;

      case 'certifications':
      case 'certs':
        setActiveSection('certifications');
        newOutput.push({ type: 'success', content: language === 'es' ? '→ Cargando certificaciones...' : '→ Loading certifications...' });
        break;

      case 'skills':
        setActiveSection('skills');
        newOutput.push({ type: 'success', content: language === 'es' ? '→ Cargando skills...' : '→ Loading skills...' });
        break;

      case 'contact':
        setActiveSection('contact');
        newOutput.push({ type: 'success', content: language === 'es' ? '→ Cargando contacto...' : '→ Loading contact...' });
        break;

      case 'htb':
        setActiveSection('htb');
        newOutput.push({ type: 'success', content: language === 'es' ? '→ Cargando estadísticas HTB...' : '→ Loading HTB stats...' });
        break;

      case 'github':
        window.open(currentData.github, '_blank', 'noopener,noreferrer');
        newOutput.push({ type: 'success', content: '→ Opening GitHub…' });
        break;

      case 'research':
      case 'bounty':
      case 'findings':
        window.open('https://c4sh3r.dev', '_blank', 'noopener,noreferrer');
        newOutput.push({
          type: 'text',
          content: language === 'es'
            ? 'Vulnerability research — hallazgos reportados en programas de divulgación\n' +
              'coordinada, validados por el equipo de seguridad de cada programa y\n' +
              'respaldados por certificado oficial con código de verificación.\n\n' +
              '  Causa raíz · impacto observado · remediación · vector CVSS 4.0\n\n' +
              '  → https://c4sh3r.dev'
            : 'Vulnerability research — findings filed through coordinated disclosure\n' +
              'programs, reviewed by each program security team and backed by an\n' +
              'official certificate with a verification code.\n\n' +
              '  Root cause · observed impact · remediation · CVSS 4.0 vector\n\n' +
              '  → https://c4sh3r.dev'
        });
        newOutput.push({ type: 'success', content: '→ Opening c4sh3r.dev…' });
        break;

      case 'linkedin':
        window.open(currentData.linkedin, '_blank', 'noopener,noreferrer');
        newOutput.push({ type: 'success', content: '→ Opening LinkedIn…' });
        break;

      case 'matrix':
        setIsMatrixMode(true);
        setTimeout(() => setIsMatrixMode(false), 5000);
        newOutput.push({ type: 'success', content: language === 'es' ? '🟢 MATRIX MODE ACTIVADO…' : '🟢 MATRIX MODE ON…' });
        break;

      case 'hack':
        newOutput.push({ type: 'hack-epic', target: trimmedCmd.split(' ')[1] || 'mainframe' });
        break;

      case 'quote':
        newOutput.push({
          type: 'text',
          content: '“' + hackingQuotes[Math.floor(Math.random() * hackingQuotes.length)] + '”'
        });
        break;

      case 'web3':
        processWeb3Command(trimmedCmd, newOutput, t);
        break;

      case 'ctf':
        setShowCTFPanel(true);
        newOutput.push({ type: 'success', content: language === 'es' ? '→ Abriendo panel CTF…' : '→ Opening CTF panel…' });
        break;

      case 'lang': {
        const targetLang = trimmedCmd.split(' ')[1];
        if (targetLang === 'es' || targetLang === 'en') {
          setLanguage(targetLang);
          storeLanguage(targetLang);
          newOutput.push({ type: 'success', content: targetLang === 'es' ? '✓ Idioma cambiado a Español' : '✓ Language switched to English' });
        } else {
          newOutput.push({ type: 'text', content: 'Usage: lang [es|en]' });
        }
        break;
      }

      case 'ls':
        newOutput.push({
          type: 'text',
          content: 'drwxr-xr-x  c4sh3r  about.txt\ndrwxr-xr-x  c4sh3r  experience/\ndrwxr-xr-x  c4sh3r  education/\ndrwxr-xr-x  c4sh3r  certifications/\ndrwxr-xr-x  c4sh3r  skills/\ndrwxr-xr-x  c4sh3r  contact.txt\ndrwxr-xr-x  c4sh3r  research/'
        });
        break;

      case 'cat':
        processCatCommand(trimmedCmd, newOutput, currentData, language);
        break;

      case 'pwd':
        newOutput.push({ type: 'text', content: '/home/c4sh3r/portfolio' });
        break;

      case 'date':
        newOutput.push({ type: 'text', content: new Date().toString() });
        break;

      default:
        newOutput.push({ type: 'error', content: `bash: ${baseCommand}: ${t.commandNotFound}` });
    }

    setOutput(newOutput);
    setInput('');
  }, [output, currentData, language, t, addToHistory, setOutput, setInput, setActiveSection, setIsMatrixMode, setShowCTFPanel, setLanguage]);

  return { processCommand };
};

// Helpers
function processWeb3Command(trimmedCmd, newOutput, t) {
  const target = trimmedCmd.split(' ')[1];
  if (!target) {
    newOutput.push({ type: 'text', content: t.web3Usage || 'Usage: web3 [contract|wallet|defi]' });
    return;
  }
  const vuln = web3Vulnerabilities[target];
  if (vuln) {
    newOutput.push({ type: 'web3', content: vuln });
  } else {
    newOutput.push({ type: 'error', content: `web3: ${target}: target not found` });
  }
}

function processCatCommand(trimmedCmd, newOutput, currentData, language) {
  const fileName = trimmedCmd.split(' ')[1];
  let fileContent;
  switch (fileName) {
    case 'about.txt':
      fileContent = `${currentData.name}\n${currentData.title}\n\n${currentData.about?.description || 'No description.'}`;
      break;
    case 'contact.txt':
      fileContent = `${language === 'es' ? 'Información de Contacto' : 'Contact Information'}\n${'='.repeat(40)}\nEmail: ${currentData.email}\nLinkedIn: ${currentData.linkedin}\nGitHub: ${currentData.github}`;
      break;
    case 'whoami.txt':
      fileContent = `${currentData.name} :: ${currentData.title}`;
      break;
    default:
      fileContent = language === 'es'
        ? `cat: ${fileName}: Archivo no encontrado\nArchivos disponibles: about.txt, contact.txt, whoami.txt`
        : `cat: ${fileName}: File not found\nAvailable files: about.txt, contact.txt, whoami.txt`;
  }
  newOutput.push({ type: 'text', content: fileContent });
}

export default useCommandProcessor;

// Suppress lint about unused import (kept for future expansions)
export { terminalCommands };
